<template>
  <div>
    <label class="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
      {{ label }}
      <span
        v-if="optional"
        class="text-xs font-normal text-gray-500 dark:text-gray-400"
      >
        ({{ $t('app.users.modal.password_optional') }})
      </span>
    </label>
    <div class="relative">
      <div
        v-if="icon"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <component
          :is="icon"
          class="h-5 w-5 text-gray-400"
        />
      </div>
      <input
        :value="modelValue"
        :type="type"
        :required="required"
        :minlength="minlength"
        :placeholder="placeholder"
        :class="inputClasses"
        @input="handleInput"
        @blur="handleBlur"
      >
    </div>
    <p
      v-if="hasError && touched"
      class="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
    >
      <ExclamationCircleIcon class="h-4 w-4" />
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline';

interface Props {
  modelValue: string;
  label: string;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  required?: boolean;
  optional?: boolean;
  minlength?: number;
  icon?: any;
  error?: string;
  touched?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  placeholder: '',
  required: false,
  optional: false,
  minlength: undefined,
  icon: undefined,
  error: '',
  touched: false
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [];
  input: [];
}>();

const hasError = computed(() => {
  return props.error && props.error.length > 0;
});

const inputClasses = computed(() => [
  'w-full px-3 py-2 sm:py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white transition-colors',
  props.icon ? 'pl-10' : 'pl-3',
  hasError.value ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-700'
]);

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
  emit('input');
}

function handleBlur() {
  emit('blur');
}
</script>
